import type { AutoScrollControl } from "./ScreenThumbBar";
import type { ReactNode } from "react";
import {
  IconCollapse,
  IconExpand,
  IconMute,
  IconPencil,
  IconClockRecord,
  IconStopSquare,
  IconVolume,
  IconVolumeOne,
  IconVolumeRadio,
} from "../control/ui/icons";
import type { InfinityId, WallId } from "@gooner/shared";

/** What the Now Showing pill says: the view's name and emoji, and — for a wall or a
 *  scroll — which one, so the pill can wear its real mark (the Premium chip, the AI
 *  sparkle) instead of an emoji stand-in. */
export interface NowShowingLabel {
  name: string;
  icon: string;
  wall?: WallId;
  infinity?: InfinityId;
}


/** One speaker body in four states, so the modes read as one control changing rather than
 *  four unrelated glyphs. Lives here with the button it serves. */
function SoundModeIcon({ mode }: { mode: "muted" | "radio" | "one" | "all" }) {
  const size = { width: 15, height: 15 };
  if (mode === "muted") return <IconMute {...size} />;
  if (mode === "radio") return <IconVolumeRadio {...size} />;
  if (mode === "one") return <IconVolumeOne {...size} />;
  return <IconVolume {...size} />;
}

/** Whether the browser can go fullscreen, and whether it currently is. */
interface FullscreenChip {
  on: boolean;
  onToggle: () => void;
}

/**
 * This screen's sound: the state the button shows, and the ways to change it.
 *
 * The button does two jobs now. Clicking it still toggles, exactly as it always did — one
 * press to silence a room is worth keeping. POINTING at it opens the master audio panel,
 * which carries the four modes, the master fader and the radio. Those are separate
 * gestures on purpose: a panel that opened and closed on the same click could never be
 * dismissed without also changing the sound.
 *
 * Reported upward rather than handled here, and handed the button element, because a
 * portaled panel has to be told where to hang and this component owns no state and adds
 * no listeners — it renders standalone in the check, with no socket, router or session.
 */
interface MuteChip {
  /** Whether the screen is quiet. The four modes collapse to this for the button's own
   *  red/not-red, the same yes/no question the remote asks. */
  on: boolean;
  /** The glyph to wear: which of the four modes is active. Absent = the plain two-state
   *  button, for any caller that has no panel to open. */
  mode?: "muted" | "radio" | "one" | "all";
  /** True while the panel this button opens is up — for aria-expanded. */
  expanded?: boolean;
  onToggle: () => void;
  /**
   * Attention arrived at the sound group — a mouse hovering it, a finger tapping it, or
   * focus landing on it. The caller decides whether that opens anything; a caller with no
   * panel simply omits it.
   *
   * The KIND rides along because the three want different treatment and only the event
   * knows which happened: a mouse opens on intent and closes when it wanders off; a
   * finger — which fires enter and leave within one tap, and can hover nothing — opens at
   * once and stays until something dismisses it; and focus opens and closes at once,
   * because a keyboard has no notion of hovering on the way past.
   */
  onOpen?: (kind: "mouse" | "touch" | "keyboard") => void;
  /** Attention left the group. Closing is the caller's business. */
  onOpenEnd?: (kind: "mouse" | "touch" | "keyboard") => void;
  /**
   * The panel this button opens, rendered as this control's own sibling.
   *
   * A NODE rather than a component, so the cluster still owns no state and reaches for
   * nothing — the screen builds the panel and hands it down, and the check renders this
   * with no panel at all. Rendered HERE rather than portaled because the panel's contents
   * are otherwise unreachable by keyboard: portaled they landed at the end of the
   * document's tab order, and the modes and fader live nowhere else.
   */
  panel?: ReactNode;
}

/**
 * A screen's middle of the site bar: what is playing, Edit, fullscreen, and the goon
 * session. This is what a screen injects where every other page shows the section links.
 *
 * ONE TEXT SIZE — `text-sm`, the size the rest of the bar already speaks (the code
 * bubble, the links, the goon button). The old ScreenBar stepped its type up at `lg:`
 * for reading across a room; a bar whose segments disagree about their own size reads
 * as three bars standing near each other.
 *
 * "NOW SHOWING" LEFT THE BAR (2026-09-09). It was a bubble here, cut to the code
 * bubble's pattern; it is the floating chip over the glass at every width now — see
 * ScreenThumbBar, which always gave the phone that chip and gives the desk the same one.
 *
 * ONE CLUSTER AT EVERY WIDTH (2026-09-09). The desk used to get a different row —
 * a gold "Goon" pill, a gear, a second pencil — above `lg:`, and the phone the glyphs
 * below it. The phone's row was the better one and it is everybody's now: the record
 * dot, the pencil, sound, fullscreen. The gear is a ☰ row everywhere, the running
 * session floats on the glass everywhere, and nothing here folds any more. The
 * paragraphs below are the history of how the phone got its row.
 *
 *
 * THE CUTOFF IS `lg:` (1024px), not `sm:`. It was `sm:`, which put a 768px iPad on the
 * desktop layout: a bar built to be read across a room, on a device held in two hands.
 * The collapsed layout simply looks better there, so the fold now happens at the width
 * where a bar genuinely has room rather than at the narrowest width where it fits. The
 * whole bar moved together — SiteBar's own nav, account and ☰ included — because a screen
 * whose controls collapsed at `lg:` while the ☰ that holds them vanished at `sm:` would
 * leave them unreachable across the entire tablet range. What changed is where the folded controls GO. This used to argue they
 * needed nowhere to go: "a phone playing a view is a screen being WATCHED, not driven",
 * with Now Showing, Edit and the session all living on the remote. That is true of a
 * television with a phone beside it and false of ONE PERSON WITH ONE PHONE, where there
 * is no second device to be the remote and the fold is the entire product. Edit, the gear
 * and the ⓘ had no second route anywhere — not on the remote either.
 *
 * So they fold from HERE and land THERE, at the bottom of the glass where a thumb reaches.
 * The pairing is a contract, not a coincidence: DisplayApp builds one `foldingControls`
 * object and renders it twice, and tools/checks/site-bar.mts asserts that every control
 * this cluster hides has a home in the thumb bar. Sound and fullscreen stay because they
 * never folded.
 *
 * THE EDIT CONTROL IS THE PENCIL ALONE. The glyph carries the verb; `aria-label` keeps
 * the sentence for a screen reader. It is on EVERY screen, including a quick setup —
 * pressing it on an unwritable view takes a copy first — and it is the way to the FULL
 * editor from a screen. (A click on a region opens that one region's feed popup on the
 * glass — collections and keywords, nothing more; layout, names and everything else
 * still live behind this pencil.)
 *
 * SOUND SITS TO ITS RIGHT, and this button is the ROOM's half of it: whether this
 * television makes noise at all. A screen defaults to muted and this is the override —
 * RED while muted, because that is the state that surprises nobody and the colour says
 * "press me to hear it"; plain once sound is on. The remote has the same toggle. WHICH
 * panel carries the audio, and how loud, is the LAYOUT's half and saves into the view
 * (see Region.soundOn) — but nothing a region says can make a muted screen speak: this
 * button is senior, a master gate over every per-region setting.
 *
 * SETTINGS SITS BETWEEN SOUND AND FULLSCREEN, because that is where the sentence puts it.
 * The cluster reads left to right from what the VIEW is (Now Showing, Edit) to what this
 * TELEVISION is (sound, party QR, claim timer) to what the BROWSER is doing (fullscreen).
 * Sound and the gear are the two facts about the box in the room, so they stand together —
 * and fullscreen stays last, where it has always been and where the hand already goes.
 *
 * It is the pencil's neighbour in shape as well: a glyph alone, with the sentence in
 * `aria-label`, and it folds at `sm:` for the reason Edit does. IconSliders rather than a
 * cog of its own, because that glyph already means "screen settings" in this codebase.
 *
 * What is deliberately NOT here, against the ScreenBar this distils: the LAYOUT button
 * (relayout is the editor's job), the DEVICE NAME (controller information, already on
 * the controller's rail), the CODE CHIP (the bar's own bubble carries it), and the
 * COLLAPSE (fullscreen is the one gesture that removes the bar, entirely).
 *
 * Presentational like the bar it slots into: every action is a prop, so the cluster
 * renders standalone in the check with no socket, no router and no session.
 */
export function ScreenControls({
  onEdit,
  editBusy = false,
  mute,
  onSettings,
  fullscreen,
  onStartSession,
  onStopSession,
  sessionActive = false,
}: {
  /** The view's own emoji and name — what this screen is playing. A wall names itself
   *  too, so the bar can wear the wall's real mark (the Premium chip) over the emoji. */
  label?: NowShowingLabel | null;
  /**
   * Opens the what's-playing picker (the same sheet the remote uses) right on this
   * screen. Present on any screen that is playing a view — a claimed device retargets
   * itself, a claimable screen swaps the view it plays in place. Null (a plain, unclickable
   * fact) only where there is no picker to open.
   */
  onLabelPress?: (() => void) | null;
  /**
   * "What am I looking at?" — opens whatever the CURRENT view has to say for itself.
   *
   * Deliberately generic rather than a wall-only button: any view could earn an
   * explanation, and the one that has one today should not teach the bar a special case.
   * The host decides both whether the button exists and what it opens; this component
   * knows only that a view can be explainable.
   *
   * Absent means no button, the same contract every other control here keeps.
   */
  onInfo?: (() => void) | null;
  /** Cycle what's playing backwards/forwards through the picker's list — the same act
   *  as the keyboard arrows. Chevrons render only when given, and only at `lg:`. */
  onPrev?: (() => void) | null;
  onNext?: (() => void) | null;
  /** Absent means no pencil — the Community Wall allows no edited version of itself,
   *  not even a forked snapshot: it is a place, and you don't take a copy of a place. */
  onEdit?: (() => void) | null;
  /** True while Edit is taking the copy that makes an unwritable view editable. */
  editBusy?: boolean;
  /** This screen's sound. Absent only where there is nothing playing to be heard. */
  mute?: MuteChip | null;
  /**
   * Opens this screen's own settings — the party QR and the claim timer.
   *
   * OPTIONAL, and absent means no button, the same contract every other control here
   * keeps. A surface with no settings to offer must not grow a gear that opens nothing,
   * and the check renders this component with `onEdit` alone to prove it.
   */
  onSettings?: (() => void) | null;
  /** Absent where the browser has no fullscreen API at all — iPhone Safari. */
  fullscreen?: FullscreenChip | null;
  /**
   * Start a session — the record dot beside the mute button, where the hand already goes.
   *
   * The RUNNING readout — timer, views, +1 Edge — hangs under the view-name chip
   * (ScreenThumbBar's SessionTab); this is the door in and out, a single tap
   * on a single glyph, at every width.
   *
   * ONE SLOT, TWO STATES. Pressing it starts a session and the same button becomes the
   * way out of one — a record dot that lights RED while recording, which is the vocabulary
   * every camera has used for fifty years. Two buttons would mean one of them is always
   * dead, in the row with the least room in the product.
   *
   * The phone's readout and its +Edge live on the glass while one runs (ScreenThumbBar);
   * this stays the start and the stop.
   */
  onStartSession?: (() => void) | null;
  onStopSession?: (() => void) | null;
  sessionActive?: boolean;
  /** The scroll's auto-scroll setting rides the shared prop bag; the ☰ row draws it
   *  (ScreenMenuItems) and the bar ignores it, like sessionActive. */
  autoScroll?: AutoScrollControl | null;
  /** Build a Slut alone: throw the picks away and play the rounds again (a ☰ row). */
  onStartOver?: (() => void) | null;
}) {
  return (
    <>
      {/* NOW SHOWING IS NOT HERE ANY MORE (2026-09-09). The pill — the view's name and
          mark, the chevrons, the ⓘ — floats over the glass at EVERY width now, the one
          chip ScreenThumbBar always gave the phone, fading on idle. A bar entry read
          across a room was the desk's version and the phone's was better: it sits on the
          picture it names, and it gets out of the way. The label props still arrive in
          `foldingControls` (one object, rendered twice) and the ☰ rows still use them. */}
      {/* THE GOON BUTTON, one glyph at every width (2026-09-09: it was the phone's alone,
          and the desk wore a gold "Goon" pill beside it — two spellings of one act). It
          sits to the LEFT of sound because the cluster reads outward from what the VIEW
          is to what the TELEVISION is, and starting a session is about the watching, not
          the box. A RUNNING session's +1 Edge hangs under the chip (ScreenThumbBar),
          at every width too. */}
      {onStartSession || onStopSession ? (
        <button
          type="button"
          onClick={sessionActive ? onStopSession ?? undefined : onStartSession ?? undefined}
          title={sessionActive ? "Stop the goon session" : "Start a goon session"}
          aria-label={sessionActive ? "Stop the goon session" : "Start a goon session"}
          aria-pressed={sessionActive}
          /* The same shape as fullscreen beside it, not a gold pill — gold is the bar's
             loudest treatment and it belongs to the RUNNING session's +Edge; wearing it
             here made "start" shout louder than the thing it starts, in a row of quiet
             glyphs. The ground stays put and only the GLYPH changes, so the button reads as
             one control in two states rather than two controls swapping places. RED in
             both states (owner, 2026-09-18): a record light is red whether it is waiting
             or lit, and the same red on the stop square says the two are one control. */
          className="rounded-full bg-white/10 p-2 text-red-400 transition-colors hover:bg-white/20"
        >
          {/* One control, two states: the clock-with-a-dot says "start the timer", the
              red square is the universal stop. The ground never changes — only the glyph
              — so it reads as a toggle rather than two buttons swapping places. */}
          {sessionActive ? <IconStopSquare width={15} height={15} /> : <IconClockRecord width={15} height={15} />}
        </button>
      ) : null}

      {/* THE PENCIL, beside the record dot, at every width. Editing is not a mid-watch
          act, but it is a ONE-TAP one, and a row of glyphs is where one-tap acts belong;
          the menu keeps the rows that need words. */}
      {onEdit ? (
        <button
          type="button"
          onClick={onEdit}
          disabled={editBusy}
          title="Edit this view"
          aria-label="Edit this view"
          className="rounded-full bg-white/10 p-2 text-zinc-200 transition-colors hover:bg-white/20 disabled:opacity-60"
        >
          <IconPencil width={15} height={15} />
        </button>
      ) : null}

      {/* Sound, for THIS screen. Red while muted — the default — so the one state worth
          changing is the one that is lit, and the glyph says WHICH of the four modes is
          running. Pointing at it opens the master audio panel; clicking it still toggles.

          It does NOT fold at `sm:`, unlike Edit and the gear beside it. It used to, on the
          argument that the remote carries the same toggle — true of a mute button, false
          of a panel with four modes, a master fader and the radio, and false anyway of a
          phone that is the only device in the room. "I cannot silence the thing making
          noise from the thing making noise" is the one absence felt in ten seconds. */}
      {mute ? (
        <span
          /*
           * POSITIONED ONLY ABOVE `sm:`, and that is what keeps the panel on a phone.
           *
           * The panel hangs `right-0` off its nearest positioned ancestor. On a desktop
           * that should be this button. On a phone it must NOT be: the button sits in the
           * bar's centre group, so a 320px panel anchored to it starts 150px off the left
           * edge of a 375px screen and half of it is unreachable (measured: off-left by
           * 152px at 375, 167px at 360).
           *
           * Going `static` below the cutoff hands the job to the bar itself, which is
           * sticky and therefore positioned and spans the viewport — so `right-0` becomes
           * the screen's right edge and `top-full` becomes "under the bar". The panel stays
           * a DOM sibling of its button either way, which is what keeps it next in tab
           * order; only the box it measures against changes.
           */
          className="static inline-flex lg:relative"
          // ONE GROUP, ONE PAIR OF HANDLERS. The panel is a child, so travelling from the
          // button down into it never leaves this element — which is what a two-source
          // "is the pointer on the button or the panel" tracker was for before the panel
          // stopped being portaled away from its own control.
          onPointerEnter={(e) => mute.onOpen?.(e.pointerType === "mouse" ? "mouse" : "touch")}
          onPointerLeave={(e) => mute.onOpenEnd?.(e.pointerType === "mouse" ? "mouse" : "touch")}
          onFocus={() => mute.onOpen?.("keyboard")}
          // Only when focus leaves the GROUP: moving from the button into the panel fires
          // a blur here too, and honouring it would shut the panel on the way in.
          onBlur={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
              mute.onOpenEnd?.("keyboard");
            }
          }}
        >
        <button
          type="button"
          onClick={mute.onToggle}
          aria-pressed={mute.on}
          aria-haspopup={mute.onOpen ? "dialog" : undefined}
          aria-expanded={mute.onOpen ? (mute.expanded ?? false) : undefined}
          title={mute.on ? "Unmute this screen" : "Mute this screen"}
          aria-label={mute.on ? "Unmute this screen" : "Mute this screen"}
          className={
            mute.on
              ? "rounded-full bg-red-600 p-2 text-white transition-colors hover:bg-red-500"
              : "rounded-full bg-white/10 p-2 text-zinc-200 transition-colors hover:bg-white/20"
          }
        >
          <SoundModeIcon mode={mute.mode ?? (mute.on ? "muted" : "all")} />
        </button>
        {mute.panel}
        </span>
      ) : null}

      {/* NO GEAR HERE. Screen settings is a ☰ row (ScreenMenuItems) at every width — a
          once-a-sitting act with words on it, which is what the menu is for. */}
      {/* Fullscreen doesn't dim the bar, it REMOVES it — so the way back is the
          browser's own gesture (Esc), not a button that would have to survive its own
          press. */}
      {fullscreen ? (
        <button
          type="button"
          onClick={fullscreen.onToggle}
          aria-pressed={fullscreen.on}
          title={fullscreen.on ? "Leave fullscreen" : "Fullscreen"}
          aria-label={fullscreen.on ? "Leave fullscreen" : "Fullscreen"}
          className="rounded-full bg-white/10 p-2 text-zinc-200 transition-colors hover:bg-white/20"
        >
          {fullscreen.on ? (
            <IconCollapse width={15} height={15} />
          ) : (
            <IconExpand width={15} height={15} />
          )}
        </button>
      ) : null}

    </>
  );
}
